import {createAsyncThunk} from "@reduxjs/toolkit";
import polarisApi from "api/polarisApi";
import type {TCriterionCreateRequest, TCriterionPatchRequest} from "api/polarisApi/types";
import {ensureOk, searchIdOf} from "./thunkUtils";

export const loadCriteria = createAsyncThunk("polaris/loadCriteria", async (_: void, {getState}) =>
	ensureOk(await polarisApi.getCriteria(searchIdOf(getState)), "Could not load criteria"),
);

// ---- Phase 4 ----

export const addCriterion = createAsyncThunk(
	"polaris/addCriterion",
	async (body: TCriterionCreateRequest, {getState, dispatch}) => {
		const created = ensureOk(
			await polarisApi.postCriterion(searchIdOf(getState), body),
			"Could not add the criterion",
		);

		await dispatch(loadCriteria());

		return created;
	},
);

// Free text → a criterion written by the backend. The list is re-read so the
// new card shows in its server position; the reducer also inserts the
// returned criterion in case that re-read fails.
export const addCriterionFromText = createAsyncThunk(
	"polaris/addCriterionFromText",
	async (text: string, {getState, dispatch}) => {
		const created = ensureOk(
			await polarisApi.postCriterionText(searchIdOf(getState), text),
			"Could not add the criterion",
		);

		await dispatch(loadCriteria());

		return created;
	},
);

export const updateCriterion = createAsyncThunk(
	"polaris/updateCriterion",
	async (arg: {criterionId: string; body: TCriterionPatchRequest}, {getState, dispatch}) => {
		const updated = ensureOk(
			await polarisApi.patchCriterion(searchIdOf(getState), arg.criterionId, arg.body),
			"Could not update the criterion",
		);

		await dispatch(loadCriteria());

		return updated;
	},
);

// The Essential flag, applied optimistically: criteriaReducers sets it on
// pending and puts it back on rejected. No list re-read, so a slower GET can't
// land over a newer flag; the PATCH answer (the criterion) is merged instead.
export const setCriterionEssential = createAsyncThunk(
	"polaris/setCriterionEssential",
	async (arg: {criterionId: string; essential: boolean}, {getState}) =>
		ensureOk(
			await polarisApi.patchCriterion(searchIdOf(getState), arg.criterionId, {
				essential: arg.essential,
			}),
			"Could not update the criterion",
		),
);

export const removeCriterion = createAsyncThunk(
	"polaris/removeCriterion",
	async (criterionId: string, {getState, dispatch}) => {
		const result = ensureOk(
			await polarisApi.deleteCriterion(searchIdOf(getState), criterionId),
			"Could not remove the criterion",
		);

		await dispatch(loadCriteria());

		return result;
	},
);

export const retryCriteriaGeneration = createAsyncThunk(
	"polaris/retryCriteriaGeneration",
	async (_: void, {getState, dispatch}) => {
		ensureOk(
			await polarisApi.retryCriteria(searchIdOf(getState)),
			"Could not retry criteria generation",
		);

		return dispatch(loadCriteria()).unwrap();
	},
);
